import { useState, useCallback } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  People as UsersIcon,
  Business as SitesIcon,
  Message as MessageIcon,
  Work as JobsIcon,
  Fax as FaxIcon,
  Rule as RulesIcon,
  Dashboard as DashboardIcon,
  Description as LogIcon,
  Notifications as NotifIcon,
  CalendarMonth as ScheduleIcon,
  PersonSearch as PatientIcon,
  Article as ReportIcon,
  Warning as ActionIcon,
  CompareArrows as DiscordanceIcon,
  GradeOutlined as PeerReviewIcon,
  Send as SentIcon,
  Quiz as QuizIcon,
  FactCheck as QCIcon,
  Person as MyIcon,
  LocalHospital as PhysicianIcon,
  School as ResidentIcon,
  SupervisorAccount as AttendingIcon,
  MarkEmailUnread as UnreadIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  Visibility as AccessedIcon,
  MedicalInformation as BiRadsIcon,
  Share as ShareIcon,
  TimerOff as SlaIcon,
  Label as LabelIcon,
  LocalFireDepartment as FireDrillIcon,
} from '@mui/icons-material';
import { useUIStore, type DynamicTab } from '../../stores/uiStore';
import { useCounters } from '../worklist/useCounters';
import { usePermissions } from '../auth/permissions';
import { gwtStyles, type TabStyle } from '../../config/theme';

/**
 * Default tabs — matches common tabs from GWT ApplicationField.
 * Additional tabs can be added from the "+" menu.
 */
const DEFAULT_TABS = [
  { id: 'studies', label: 'Studies' },
  { id: 'newstudy', label: 'New Studies' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'dictated', label: 'Dictated' },
  { id: 'read', label: 'Read' },
  { id: 'urgent', label: 'Urgent' },
  { id: 'onhold', label: 'On Hold' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'inprogress', label: 'In Progress' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'transcribed', label: 'Transcribed' },
  { id: 'ordered', label: 'Ordered' },
  { id: 'scheduled', label: 'Scheduled' },
];

/** Worklist view tabs available from the "+" menu, grouped by category */
const WORKLIST_VIEW_DEFS: { id: string; label: string; icon: React.ReactNode; group: string }[] = [
  // Global / Admin views
  { id: 'actionneeded', label: 'Action Needed', icon: <ActionIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'discordance', label: 'Discordance', icon: <DiscordanceIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'peerreview', label: 'Peer Review', icon: <PeerReviewIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'queued', label: 'Queued', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'sent', label: 'Sent', icon: <SentIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'hasquiz', label: 'Has Quiz', icon: <QuizIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'qcall', label: 'QC All', icon: <QCIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'unsigned', label: 'Unsigned', icon: <ReportIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'signed', label: 'Signed', icon: <ReportIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'unread', label: 'All Unread', icon: <UnreadIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'today', label: 'Updated Today', icon: <TodayIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'lastweek', label: 'Updated Last Week', icon: <DateRangeIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'accessed', label: 'Accessed by Me', icon: <AccessedIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'has_bi_rads', label: 'Has BI-RADS', icon: <BiRadsIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'shared_by_me', label: 'Shared By Me', icon: <ShareIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'shared_with_me', label: 'Shared With Me', icon: <ShareIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  { id: 'ssl_late', label: 'SLA Late', icon: <SlaIcon sx={{ fontSize: 16 }} />, group: 'Study Views' },
  // "My" radiologist views
  { id: 'myunread', label: 'My Unread', icon: <UnreadIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  { id: 'myactionneeded', label: 'My Action Needed', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  { id: 'myqueued', label: 'My Queued', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  { id: 'mydelivered', label: 'My Delivered', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  { id: 'myread', label: 'My Read', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  { id: 'mydictated', label: 'My Dictated', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  { id: 'myunsigned', label: 'My Unsigned', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  { id: 'mysigned', label: 'My Signed', icon: <MyIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  // Physician
  { id: 'mypatients', label: 'My Patients', icon: <PhysicianIcon sx={{ fontSize: 16 }} />, group: 'My Work' },
  // Resident views
  { id: 'myqueued_resident', label: 'My Queued (Resident)', icon: <ResidentIcon sx={{ fontSize: 16 }} />, group: 'Resident' },
  { id: 'myreported_resident', label: 'My Reported (Resident)', icon: <ResidentIcon sx={{ fontSize: 16 }} />, group: 'Resident' },
  { id: 'myscored_resident', label: 'My Scored (Resident)', icon: <ResidentIcon sx={{ fontSize: 16 }} />, group: 'Resident' },
  // Attending → Resident views
  { id: 'myresident_assigned', label: 'Resident Assigned', icon: <AttendingIcon sx={{ fontSize: 16 }} />, group: 'Attending' },
  { id: 'myresident_reported', label: 'Resident Reported', icon: <AttendingIcon sx={{ fontSize: 16 }} />, group: 'Attending' },
  { id: 'myresident_scored', label: 'Resident Scored', icon: <AttendingIcon sx={{ fontSize: 16 }} />, group: 'Attending' },
];

/** Admin tab definitions for the "+" menu */
const ADMIN_TAB_DEFS: { id: string; label: string; adminPanel: string; icon: React.ReactNode; requireAdmin?: boolean }[] = [
  { id: 'admin-users', label: 'Users', adminPanel: 'users', icon: <UsersIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-sites', label: 'Sites', adminPanel: 'sites', icon: <SitesIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-hl7', label: 'HL7 Queue', adminPanel: 'hl7queue', icon: <MessageIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-jobs', label: 'Jobs', adminPanel: 'jobs', icon: <JobsIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-fax', label: 'Fax Queue', adminPanel: 'faxqueue', icon: <FaxIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-rules', label: 'Rules', adminPanel: 'rules', icon: <RulesIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-summary', label: 'System Summary', adminPanel: 'systemsummary', icon: <DashboardIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-log', label: 'Log Viewer', adminPanel: 'logviewer', icon: <LogIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-notif', label: 'Notifications', adminPanel: 'notifications', icon: <NotifIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-labels', label: 'System Labels', adminPanel: 'systemlabels', icon: <LabelIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'admin-firedrill', label: 'Fire Drill', adminPanel: 'firedrill', icon: <FireDrillIcon sx={{ fontSize: 16 }} />, requireAdmin: true },
  { id: 'messaging', label: 'Messages', adminPanel: '', icon: <MessageIcon sx={{ fontSize: 16 }} /> },
  { id: 'scheduling', label: 'Schedule', adminPanel: '', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
  { id: 'patients', label: 'Patients', adminPanel: '', icon: <PatientIcon sx={{ fontSize: 16 }} /> },
  { id: 'reports', label: 'Reports', adminPanel: '', icon: <ReportIcon sx={{ fontSize: 16 }} /> },
  { id: 'admin-protocols', label: 'Protocols', adminPanel: 'protocols', icon: <RulesIcon sx={{ fontSize: 16 }} /> },
];

/**
 * Tab bar — supports two styles via `gwtStyles.tabStyle`:
 *
 *   - 'classic' — GWT-matching rounded corners, gradients, border overlap
 *   - 'flat'    — modern minimal underline indicator, no rounded corners
 *
 * Dark theme defaults to 'flat'; legacy theme defaults to 'classic'.
 */
export const TabBar = () => {
  const activeTab = useUIStore((s) => s.activeTab);
  const setActiveTab = useUIStore((s) => s.setActiveTab);
  const dynamicTabs = useUIStore((s) => s.dynamicTabs);
  const openDynamicTab = useUIStore((s) => s.openDynamicTab);
  const closeDynamicTab = useUIStore((s) => s.closeDynamicTab);
  const { data: counters } = useCounters();
  const perms = usePermissions();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const handleOpenMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleOpenAdminTab = useCallback(
    (def: typeof ADMIN_TAB_DEFS[0]) => {
      let type: DynamicTab['type'] = 'admin';
      if (def.id === 'messaging') type = 'messaging';
      else if (def.id === 'scheduling') type = 'scheduling';
      else if (def.id === 'patients') type = 'patients';
      else if (def.id === 'reports') type = 'reports';

      openDynamicTab({
        id: def.id,
        label: def.label,
        type,
        adminPanel: def.adminPanel || undefined,
        closeable: true,
      });
      handleCloseMenu();
    },
    [openDynamicTab, handleCloseMenu],
  );

  const handleOpenWorklistView = useCallback(
    (def: typeof WORKLIST_VIEW_DEFS[0]) => {
      openDynamicTab({
        id: def.id,
        label: def.label,
        type: 'worklist',
        closeable: true,
      });
      handleCloseMenu();
    },
    [openDynamicTab, handleCloseMenu],
  );

  /** Build menu items with group headers and dividers */
  const menuItems = useCallback(() => {
    const items: React.ReactNode[] = [];

    // Worklist views grouped by category
    let lastGroup = '';
    for (const def of WORKLIST_VIEW_DEFS) {
      if (def.group !== lastGroup) {
        if (lastGroup !== '') {
          items.push(<Divider key={`div-${def.group}`} />);
        }
        items.push(
          <MenuItem key={`hdr-${def.group}`} disabled sx={{ fontSize: 11, py: 0.25, fontWeight: 600, opacity: '0.7 !important' }}>
            {def.group}
          </MenuItem>,
        );
        lastGroup = def.group;
      }
      items.push(
        <MenuItem key={def.id} onClick={() => handleOpenWorklistView(def)} sx={{ fontSize: 12, py: 0.5, pl: 4 }}>
          <ListItemIcon sx={{ minWidth: 28 }}>{def.icon}</ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 12 }}>{def.label}</ListItemText>
        </MenuItem>,
      );
    }

    // Divider before admin/other tabs
    const visibleAdminTabs = ADMIN_TAB_DEFS.filter((def) => !def.requireAdmin || perms.isAdmin);

    if (visibleAdminTabs.length > 0) {
      items.push(<Divider key="div-admin" />);
      items.push(
        <MenuItem key="hdr-admin" disabled sx={{ fontSize: 11, py: 0.25, fontWeight: 600, opacity: '0.7 !important' }}>
          Admin / Other
        </MenuItem>,
      );

      for (const def of visibleAdminTabs) {
        items.push(
          <MenuItem key={def.id} onClick={() => handleOpenAdminTab(def)} sx={{ fontSize: 12, py: 0.5, pl: 4 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>{def.icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 12 }}>{def.label}</ListItemText>
          </MenuItem>,
        );
      }
    }

    return items;
  }, [handleOpenWorklistView, handleOpenAdminTab, perms.isAdmin]);

  return (
    <Box
      sx={{
        height: 32,
        display: 'flex',
        alignItems: 'flex-end',
        px: 0.5,
        backgroundColor: gwtStyles.mainView,
        // Bottom border line matching GWT — the tab content area border
        borderBottom: `1px solid ${gwtStyles.popupBorder}`,
        overflowX: 'auto',
        overflowY: 'hidden',
        // Thin scrollbar for horizontal tab overflow
        '&::-webkit-scrollbar': { height: 3 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: gwtStyles.popupBorder,
          borderRadius: 2,
        },
        scrollbarWidth: 'thin',
      }}
    >
      {/* Static worklist tabs */}
      {DEFAULT_TABS.map((tab) => {
        const isSelected = activeTab === tab.id;
        const count = counters?.[tab.id] ?? 0;
        return (
          <TabChip
            key={tab.id}
            label={tab.label}
            badge={count > 0 ? count : undefined}
            isSelected={isSelected}
            onClick={() => setActiveTab(tab.id)}
          />
        );
      })}

      {/* Dynamic tabs */}
      {dynamicTabs.map((tab) => {
        const isSelected = activeTab === tab.id;
        return (
          <TabChip
            key={tab.id}
            label={tab.label}
            isSelected={isSelected}
            onClick={() => setActiveTab(tab.id)}
            closeable={tab.closeable}
            onClose={() => closeDynamicTab(tab.id)}
          />
        );
      })}

      {/* "+" button to open admin/other tabs */}
      <Box
        sx={{
          height: (gwtStyles.tabStyle as TabStyle) === 'flat' ? 32 : 28,
          display: 'flex',
          alignItems: 'center',
          ml: 0.5,
          mb: (gwtStyles.tabStyle as TabStyle) === 'flat' ? 0 : '1px',
        }}
      >
        <IconButton
          size="small"
          onClick={handleOpenMenu}
          sx={(gwtStyles.tabStyle as TabStyle) === 'flat' ? {
            width: 24,
            height: 24,
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
          } : {
            width: 24,
            height: 24,
            borderRadius: '6px 6px 0 0',
            border: `1px solid ${gwtStyles.popupBorder}`,
            borderBottom: 'none',
            backgroundColor: gwtStyles.tabGradientStart,
            '&:hover': { backgroundColor: gwtStyles.tabGradientEnd },
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            border: `1px solid ${gwtStyles.popupBorder}`,
            borderRadius: '6px',
            backgroundColor: gwtStyles.popupBg,
            maxHeight: 500,
          },
        }}
      >
        {menuItems()}
      </Menu>
    </Box>
  );
};

/* ── Tab chip sub-component ── */
interface TabChipProps {
  label: string;
  badge?: number;
  isSelected: boolean;
  onClick: () => void;
  closeable?: boolean;
  onClose?: () => void;
}

const TabChip = ({ label, badge, isSelected, onClick, closeable, onClose }: TabChipProps) => {
  const flat = (gwtStyles.tabStyle as TabStyle) === 'flat';

  return (
    <Box
      onClick={onClick}
      sx={flat ? {
        /* ── Flat / modern style ── */
        height: 32,
        minWidth: 80,
        maxWidth: 200,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: closeable ? 0.75 : 1.5,
        mr: '2px',
        cursor: 'pointer',
        userSelect: 'none',
        borderRadius: 0,
        border: 'none',
        borderBottom: isSelected
          ? `2px solid ${gwtStyles.accentPrimary}`
          : '2px solid transparent',
        background: 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
          backgroundColor: isSelected ? 'transparent' : 'rgba(255,255,255,0.04)',
          borderBottomColor: isSelected ? gwtStyles.accentPrimary : gwtStyles.borderSubtle,
        },
      } : {
        /* ── Classic GWT style (rounded corners + gradients) ── */
        height: isSelected ? 31 : 28,
        minWidth: 80,
        maxWidth: 200,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: closeable ? 0.75 : 1.5,
        mr: '4px',
        cursor: 'pointer',
        userSelect: 'none',
        borderTopLeftRadius: '6px',
        borderTopRightRadius: '6px',
        border: `1px solid ${gwtStyles.popupBorder}`,
        borderBottom: isSelected ? `1px solid ${gwtStyles.tabContent}` : `1px solid ${gwtStyles.popupBorder}`,
        background: isSelected
          ? `linear-gradient(${gwtStyles.tabGradientStart}, ${gwtStyles.tabSelectedEnd})`
          : `linear-gradient(${gwtStyles.tabGradientStart}, ${gwtStyles.tabGradientEnd})`,
        position: 'relative',
        bottom: isSelected ? -1 : 0,
        zIndex: isSelected ? 1 : 0,
        '&:hover': {
          opacity: isSelected ? 1 : 0.85,
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: 12,
          fontWeight: isSelected ? 600 : 400,
          color: isSelected && flat ? gwtStyles.accentPrimary : 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
        {badge != null && badge > 0 && ` (${badge})`}
      </Typography>
      {closeable && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          sx={{ ml: 0.25, width: 16, height: 16, p: 0 }}
        >
          <CloseIcon sx={{ fontSize: 10 }} />
        </IconButton>
      )}
    </Box>
  );
};
